import Order, { OrderSelection } from '~/types/Order';
import OrdersBatch, {
  BatchEntry,
  BatchShipment,
  BatchSteps,
  BatchTransaction,
  Parcel,
  UserBatchInput,
} from '~/types/OrdersBatch';
import { ActionKeys } from './constants';
import { TrackingReference } from '~/utils/TrackingHelper/types';

export interface FetchOrdersBatchPayload {
  batch: OrdersBatch;
  currentShipments?: HashMap<string, BatchShipment>;
  currentBatchEntries?: HashMap<number, BatchEntry>;
  currentTransactions?: HashMap<string, BatchTransaction>;
  trackingReference?: TrackingReference;
}

export function fetchOrdersBatch(batchId: number) {
  return <const>{
    batchId,
    type: ActionKeys.ORDERS_BATCH_FETCH,
  };
}

export function fetchOrdersBatchSuccess(payload: FetchOrdersBatchPayload) {
  return <const>{
    payload,
    type: ActionKeys.ORDERS_BATCH_FETCH_SUCCESS,
  };
}

export function fetchOrdersBatchError() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_FETCH_ERROR,
  };
}

export function pollBatchOrdersStart(batchId: number, trackingReference?: TrackingReference) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_POLL_START,
    payload: {
      batchId,
      trackingReference,
    },
  };
}

export function pollBatchOrdersEnd() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_POLL_END,
  };
}

export function pollBatchOrdersTimeout() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_POLL_TIMEOUT,
  };
}

export function savePackageInformation(batchId: number, shipmentGenerationRequest: UserBatchInput) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION,
    payload: {
      batchId,
      shipmentGenerationRequest,
    },
  };
}

export function savePackageInformationSuccess() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION_SUCCESS,
  };
}

export function savePackageInformationError() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION_ERROR,
  };
}

export function regenerateBatchEntry(
  batchId: number,
  batchEntryId: number,
  shipmentGenerationRequest: UserBatchInput,
  shipmentId: string,
  orderObjectId: string,
) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRY_REGENERATE,
    payload: {
      batchId,
      batchEntryId,
      shipmentGenerationRequest,
      shipmentId,
      orderObjectId,
    },
  };
}

export function regenerateBatchEntrySuccess(batchEntryId) {
  return <const>{
    batchEntryId,
    type: ActionKeys.ORDERS_BATCH_ENTRY_REGENERATE_SUCCESS,
  };
}

export function regenerateBatchEntryError() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_ENTRY_REGENERATE_ERROR,
  };
}

export function createBatchTransactions(
  batchId: number,
  rates: string[],
  trackingReference: TrackingReference,
) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS,
    payload: {
      batchId,
      rates,
      trackingReference,
    },
  };
}

export function createBatchTransactionsSuccess(rateCount: number) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_SUCCESS,
    payload: {
      rateCount,
    },
  };
}

export function createBatchTransactionsError(error: string) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_ERROR,
    payload: {
      error,
    },
  };
}

export function changeBatchStep(step: BatchSteps) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_STEP_CHANGE,
    payload: {
      step,
    },
  };
}

export function removeOrderFromBatch(orderId: string) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_REMOVE_ORDER,
    payload: {
      orderId,
    },
  };
}

export function generatePackingSlipsFromBatch() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS,
  };
}

export interface DownloadPackingSlipsPayload {
  slipUrl: string;
}

export function generatePackingSlipsFromBatchSuccess(payload: DownloadPackingSlipsPayload) {
  return <const>{
    payload,
    type: ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_SUCCESS,
  };
}

export function generatePackingSlipsFromBatchError(error: string) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_ERROR,
    payload: {
      error,
    },
  };
}

export function generateAllLabels() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS,
  };
}

export interface DownloadAllLabelsPayload {
  labelsUrl: string;
}

export function generateAllLabelsSuccess(payload: DownloadAllLabelsPayload) {
  return <const>{
    payload,
    type: ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_SUCCESS,
  };
}

export function generateAllLabelsError(error: string) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_ERROR,
    payload: {
      error,
    },
  };
}

export function changeAllRates(serviceLevelToken: string) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_CHANGE_ALL_RATES,
    payload: {
      serviceLevelToken,
    },
  };
}

export function fetchInvalidOrders() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_FETCH_INVALID_ORDERS,
  };
}

export function cleanUpBatchStore() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_CLEAN_UP,
  };
}

export function openBatchInlineEditModal(orderId: string) {
  return <const>{
    orderId,
    type: ActionKeys.ORDERS_BATCH_OPEN_INLINE_EDIT_MODAL,
  };
}

export function closeBatchLineEditModal() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_CLOSE_INLINE_EDIT_MODAL,
  };
}

export function slideBatchInlineEditModal() {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_SLIDE_INLINE_EDIT_MODAL,
  };
}

export function updateBatchOrder(
  order: Order,
  formName: string,
  parcel?: Parcel,
  useImportedWeight?: boolean,
  isUpdatingAddress?: boolean,
) {
  return <const>{
    formName,
    useImportedWeight,
    isUpdatingAddress,
    type: ActionKeys.ORDERS_BATCH_UPDATE_ORDER,
    payload: {
      order,
      parcel,
    },
  };
}

export function updateBatchOrderSuccess(order: Order) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_UPDATE_ORDER_SUCCESS,
    payload: {
      order,
    },
  };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function updateBatchOrderError(error: any) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_UPDATE_ORDER_ERROR,
    payload: {
      error,
    },
  };
}

export function removeSelectedRate(shipmentId: string) {
  return <const>{
    shipmentId,
    type: ActionKeys.ORDERS_BATCH_REMOVE_SELECTED_RATE,
  };
}

export function fetchUserBatchInput(batchEntryId: number) {
  return <const>{
    batchEntryId,
    type: ActionKeys.ORDERS_BATCH_FETCH_USER_INPUT,
  };
}

export function fetchUserBatchInputSuccess(batchEntryId: number, userBatchInput: UserBatchInput) {
  return <const>{
    batchEntryId,
    type: ActionKeys.ORDERS_BATCH_FETCH_USER_INPUT_SUCCESS,
    payload: {
      userBatchInput,
    },
  };
}

export function fetchUserBatchInputError(batchEntryId, errorMessage: string) {
  return <const>{
    batchEntryId,
    errorMessage,
    type: ActionKeys.ORDERS_BATCH_FETCH_USER_INPUT_ERROR,
  };
}

export function ordersFetchTriggered(ordersFetched: string[]) {
  return <const>{
    ordersFetched,
    type: ActionKeys.ORDERS_BATCH_ORDERS_FETCH_TRIGGERED,
  };
}

export function createAndBuyBatch(
  orderSelection: OrderSelection,
  rateObjectIds: string[],
  trackingReference: TrackingReference,
) {
  return <const>{
    type: ActionKeys.ORDERS_BATCH_CREATE_AND_BUY,
    payload: {
      orderSelection,
      rateObjectIds,
      trackingReference,
    },
  };
}

export function fetchTransactionData(remainingLabels: number, labelRenewalDate: string) {
  return <const>{
    payload: {
      remainingLabels,
      labelRenewalDate,
    },
    type: ActionKeys.ORDERS_BATCH_TRANSACTION_DATA,
  };
}

export type OrdersBatchActions =
  | ReturnType<typeof fetchOrdersBatch>
  | ReturnType<typeof fetchOrdersBatchSuccess>
  | ReturnType<typeof fetchOrdersBatchError>
  | ReturnType<typeof pollBatchOrdersStart>
  | ReturnType<typeof pollBatchOrdersEnd>
  | ReturnType<typeof pollBatchOrdersTimeout>
  | ReturnType<typeof savePackageInformation>
  | ReturnType<typeof savePackageInformationSuccess>
  | ReturnType<typeof savePackageInformationError>
  | ReturnType<typeof regenerateBatchEntry>
  | ReturnType<typeof regenerateBatchEntrySuccess>
  | ReturnType<typeof regenerateBatchEntryError>
  | ReturnType<typeof createBatchTransactions>
  | ReturnType<typeof createBatchTransactionsSuccess>
  | ReturnType<typeof createBatchTransactionsError>
  | ReturnType<typeof changeBatchStep>
  | ReturnType<typeof removeOrderFromBatch>
  | ReturnType<typeof generatePackingSlipsFromBatch>
  | ReturnType<typeof generatePackingSlipsFromBatchSuccess>
  | ReturnType<typeof generatePackingSlipsFromBatchError>
  | ReturnType<typeof generateAllLabels>
  | ReturnType<typeof generateAllLabelsSuccess>
  | ReturnType<typeof generateAllLabelsError>
  | ReturnType<typeof changeAllRates>
  | ReturnType<typeof fetchInvalidOrders>
  | ReturnType<typeof cleanUpBatchStore>
  | ReturnType<typeof openBatchInlineEditModal>
  | ReturnType<typeof closeBatchLineEditModal>
  | ReturnType<typeof slideBatchInlineEditModal>
  | ReturnType<typeof updateBatchOrder>
  | ReturnType<typeof updateBatchOrderSuccess>
  | ReturnType<typeof updateBatchOrderError>
  | ReturnType<typeof removeSelectedRate>
  | ReturnType<typeof fetchUserBatchInput>
  | ReturnType<typeof fetchUserBatchInputSuccess>
  | ReturnType<typeof fetchUserBatchInputError>
  | ReturnType<typeof ordersFetchTriggered>
  | ReturnType<typeof createAndBuyBatch>
  | ReturnType<typeof fetchTransactionData>;
